import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper, 
  Button,
  Card, 
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import GroupsIcon from '@mui/icons-material/Groups';
import Image from '../components/Image';

// Import images from centralized image config
import { BANNERS, SERVICES } from '../assets/images/imageConfig';

// Images from the centralized config
const SERVICES_BANNER = BANNERS.SERVICES;
const SECURITY_GUARD = SERVICES.SECURITY;
const EVENT_SECURITY = SERVICES.EVENT_SECURITY;
const VIP_SECURITY = SERVICES.VIP_SECURITY;

const Services = () => {
  const securityServices = [
    { 
      title: "Residential Security",
      icon: <HomeIcon />,
      description: "Professional security guards to protect residential properties, apartments, and gated communities.",
      features: [
        "24/7 premises monitoring",
        "Visitor management",
        "Parking management",
        "Emergency response",
        "Regular security patrols",
        "Access control management"
      ]
    },
    { 
      title: "Commercial Security",
      icon: <BusinessIcon />,
      description: "Comprehensive security solutions for offices, retail establishments, and commercial properties.",
      features: [
        "Access control systems",
        "CCTV monitoring",
        "Theft prevention",
        "Employee and visitor verification",
        "After-hours security",
        "Emergency protocols"
      ]
    },
    { 
      title: "Event Security",
      icon: <EventIcon />,
      description: "Professional security personnel for events, concerts, exhibitions, and private functions.",
      features: [
        "Crowd management",
        "VIP protection",
        "Access control",
        "Emergency response planning",
        "Threat assessment",
        "Coordination with local authorities"
      ]
    },
    { 
      title: "Industrial Security",
      icon: <FactoryIcon />,
      description: "Specialized security solutions for factories, warehouses, and industrial facilities.",
      features: [
        "Perimeter security",
        "Asset protection",
        "Hazardous materials security",
        "Fire safety monitoring",
        "Emergency evacuation assistance",
        "Equipment and inventory protection"
      ]
    }
  ];

  return (
    <Box>
      {/* Hero Banner */}
      <Box 
        sx={{
          position: 'relative',
          height: { xs: '200px', md: '400px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 40, 85, 0.8)',
              zIndex: 1
            }
          }}
        >
          <Image 
            src={SERVICES_BANNER}
            alt="Security Services"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
        <Box sx={{ position: 'relative', textAlign: 'center', zIndex: 1, px: 2 }}>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3.5rem' } 
            }}
          >
            Security Services
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 800, mx: 'auto' }}>
            Professional physical security solutions to protect your people, assets, and premises
          </Typography>
        </Box>
      </Box>

      {/* Introduction */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            color="primary"
            sx={{ fontWeight: 700 }}
          >
            Professional Security Solutions
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mb: 2 }}>
            SafeNext provides comprehensive physical security services tailored to your specific needs
          </Typography>
          <Divider sx={{ width: 80, mx: 'auto', borderColor: 'primary.main', borderWidth: 2 }} />
        </Box>

        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
              Why Choose SafeNext Security?
            </Typography>
            <Typography paragraph>
              At SafeNext, we understand that security is not just about physical presence but a comprehensive 
              approach to safety, risk management, and peace of mind. We provide professionally trained security 
              personnel who are equipped with the skills and knowledge to handle various security scenarios.
            </Typography>
            <Typography paragraph>
              With over a decade of experience in the security industry, we have established ourselves as a 
              trusted provider of security services for various sectors including residential, commercial, 
              industrial, and events.
            </Typography>
            <List>
              {[
                "Professionally trained and vetted security guards",
                "Customized security solutions for your specific needs",
                "24/7 operational support and supervision",
                "Quick response to security incidents",
                "Regular security assessments and reports"
              ].map((item, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircleOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box 
              component="img"
              src={SECURITY_GUARD}
              alt="Professional Security Guard"
              sx={{ 
                width: '100%', 
                height: 'auto', 
                borderRadius: 2,
                boxShadow: 3 
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Our Security Services */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.alternate' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              color="primary"
              sx={{ fontWeight: 700 }}
            >
              Our Security Services
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mb: 2 }}>
              Comprehensive security solutions for various sectors and requirements
            </Typography>
            <Divider sx={{ width: 80, mx: 'auto', borderColor: 'primary.main', borderWidth: 2 }} />
          </Box>

          <Grid container spacing={4}>
            {securityServices.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card 
                  elevation={3}
                  sx={{ 
                    height: '100%', 
                    borderRadius: 4,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{ 
                          bgcolor: 'primary.main',
                          mr: 2
                        }}
                      >
                        {service.icon}
                      </Avatar>
                      <Typography variant="h5" component="h3" fontWeight={600}>
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography paragraph color="text.secondary">
                      {service.description}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Key Features:
                    </Typography>
                    <List disablePadding>
                      {service.features.map((feature, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature} 
                            primaryTypographyProps={{ 
                              variant: 'body2',
                              color: 'text.secondary'
                            }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      component={Link}
                      to="/contact"
                      size="small"
                    >
                      Learn More
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Featured Security Solutions */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            color="primary"
            sx={{ fontWeight: 700 }}
          >
            Featured Security Solutions
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mb: 2 }}>
            Our specialized security services designed for specific needs
          </Typography>
          <Divider sx={{ width: 80, mx: 'auto', borderColor: 'primary.main', borderWidth: 2 }} />
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={3}
              sx={{ 
                borderRadius: 4,
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ height: 250, overflow: 'hidden' }}>
                <Image
                  src={EVENT_SECURITY}
                  alt="Event Security"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Box sx={{ p: 3, flexGrow: 1 }}>
                <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
                  Event Security Management
                </Typography>
                <Typography paragraph color="text.secondary">
                  Our event security services provide comprehensive protection for events of all sizes, 
                  from small private gatherings to large public concerts and exhibitions. We handle:
                </Typography>
                <List>
                  {[
                    "Crowd control and management",
                    "Access control and ticket verification",
                    "VIP protection and escorting",
                    "Emergency response planning",
                    "Coordination with venue staff and authorities"
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
                <Button 
                  variant="contained" 
                  component={Link}
                  to="/contact"
                  sx={{ mt: 2 }}
                >
                  Inquire About Event Security
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={3}
              sx={{ 
                borderRadius: 4,
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ height: 250, overflow: 'hidden' }}>
                <Image
                  src={VIP_SECURITY}
                  alt="VIP Security"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Box sx={{ p: 3, flexGrow: 1 }}>
                <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
                  VIP Protection Services
                </Typography>
                <Typography paragraph color="text.secondary">
                  Our specialized VIP protection team provides discreet and effective security for executives, 
                  celebrities, and high-profile individuals. Our services include:
                </Typography>
                <List>
                  {[
                    "Personal bodyguard services",
                    "Secure transportation arrangements",
                    "Advance location security assessment",
                    "Threat assessment and risk management",
                    "24/7 close protection services"
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
                <Button 
                  variant="contained" 
                  component={Link}
                  to="/contact"
                  sx={{ mt: 2 }}
                >
                  Inquire About VIP Protection
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Call to Action */}
      <Box 
        sx={{
          bgcolor: 'primary.main',
          py: { xs: 6, md: 8 },
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Need Customized Security Solutions?
              </Typography>
              <Typography variant="h6" gutterBottom>
                We tailor our security services to meet your specific requirements
              </Typography>
              <Typography>
                Contact us for a free consultation and security assessment. Our team will work with you to 
                develop a comprehensive security plan that addresses your unique challenges and needs.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)'
                  }
                }}
              >
                Contact Us Today
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

// Missing MUI icons definitions
const HomeIcon = () => <ShieldIcon />;
const BusinessIcon = () => <SecurityIcon />;
const EventIcon = () => <GroupsIcon />;
const FactoryIcon = () => <LocalPoliceIcon />;

export default Services; 